import React, { useState } from "react";
import { useQuery, useLazyQuery } from "@apollo/react-hooks";
import { httpClient } from "settings/apolloClient";
import SHIPMENT_REQUESTS_QUERY from "./graphql/shipmentRequestsQuery";
import SHIPMENT_REQUEST_PAYLOAD_QUERY from "./graphql/shipmentRequestPayloadQuery";
import {
  PAGE_SIZE,
  SHIPMENT_STATUS,
  formatDateTime,
  formatVolume,
  Card,
  Scroll,
  Table,
  Th,
  Tr,
  Td,
  Code,
  Sub,
  Mono,
  MsgCell,
  Dash,
  Empty,
  C,
  StatusPill,
  Drawer,
  DrawerHead,
  DrawerBody,
  Eyebrow,
  DrawerTitle,
  HeadMeta,
  HeadGrow,
  CloseX,
  SectionLabel,
  KV,
  Callout,
  Pagination,
  PayloadSection,
  CodeResolutionSection,
} from "./requestsShared";

const ShipmentRequestsPanel = ({ status }) => {
  const [page, setPage] = useState(0);
  const [selected, setSelected] = useState(null);

  const { data, loading } = useQuery(SHIPMENT_REQUESTS_QUERY, {
    variables: {
      limit: PAGE_SIZE, offset: page * PAGE_SIZE, orderBy: "desc", status: status || null,
    },
    client: httpClient,
    fetchPolicy: "network-only",
  });

  const [loadPayload, payloadState] = useLazyQuery(SHIPMENT_REQUEST_PAYLOAD_QUERY, {
    client: httpClient,
    fetchPolicy: "network-only",
  });

  const rows = (data && data.shipmentRequests) || [];
  const payload = payloadState.data && payloadState.data.shipmentRequest;

  const openRow = (row) => {
    setSelected(row);
    loadPayload({ variables: { id: row.id } });
  };

  const close = () => setSelected(null);

  return (
    <Card>
      <Scroll>
        <Table>
          <thead>
            <tr>
              <Th>Código</Th>
              <Th>Recebida em</Th>
              <Th>Volume</Th>
              <Th>Obra</Th>
              <Th>Status</Th>
              <Th>Mensagem</Th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <Tr key={r.id} selected={selected && selected.id === r.id} onClick={() => openRow(r)}>
                <Td>
                  <Code>{r.code || "—"}</Code>
                  <Sub>{`#${r.id}`}</Sub>
                </Td>
                <Td><Mono>{formatDateTime(r.insertedAt)}</Mono></Td>
                <Td><Mono color={C.inkSoft}>{formatVolume(r.quantity)}</Mono></Td>
                <Td>{r.constructionName || <Dash>—</Dash>}</Td>
                <Td><StatusPill config={SHIPMENT_STATUS} value={r.status} /></Td>
                <Td>{r.statusMessage ? <MsgCell>{r.statusMessage}</MsgCell> : <Dash>—</Dash>}</Td>
              </Tr>
            ))}
          </tbody>
        </Table>
      </Scroll>
      {!loading && rows.length === 0 && <Empty>Nenhuma requisição encontrada.</Empty>}
      <Pagination
        page={page}
        count={rows.length}
        onPrev={() => setPage((p) => Math.max(0, p - 1))}
        onNext={() => setPage((p) => p + 1)}
      />

      {selected && (
        <Drawer onClose={close}>
          <DrawerHead>
            <HeadGrow>
              <Eyebrow>Requisição de carga · ERP</Eyebrow>
              <DrawerTitle>{selected.code || `#${selected.id}`}</DrawerTitle>
              <HeadMeta>
                <StatusPill config={SHIPMENT_STATUS} value={selected.status} />
                <Mono>{formatDateTime(selected.insertedAt)}</Mono>
              </HeadMeta>
            </HeadGrow>
            <CloseX onClick={close}>✕</CloseX>
          </DrawerHead>
          <DrawerBody>
            <SectionLabel first>Resumo</SectionLabel>
            <KV>
              <dt>ID</dt>
              <dd>{`#${selected.id}`}</dd>
              <dt>Obra</dt>
              <dd>{selected.constructionName || "—"}</dd>
              <dt>Volume</dt>
              <dd>{formatVolume(selected.quantity)}</dd>
              <dt>Operador</dt>
              <dd>{selected.operatorName || "—"}</dd>
              <dt>Equipamento</dt>
              <dd>{selected.equipmentCode || "—"}</dd>
            </KV>

            {selected.statusMessage && (
              <React.Fragment>
                <SectionLabel>Mensagem</SectionLabel>
                <Callout variant={(SHIPMENT_STATUS[selected.status] || {}).variant || "idle"}>
                  {selected.statusMessage}
                </Callout>
              </React.Fragment>
            )}

            <CodeResolutionSection
              materials={payload && payload.materials}
              loading={payloadState.loading}
            />

            <PayloadSection
              label="Payload original"
              note="como recebido do ERP · antes da normalização"
              value={payload && payload.originalPayload}
              loading={payloadState.loading}
            />
          </DrawerBody>
        </Drawer>
      )}
    </Card>
  );
};

export default ShipmentRequestsPanel;
